// This screen differs from the original as it now handles both forwarding the user to the AddCard
// screen, and can handle being the last screen in the booking flow where we create the appointment
// and patient objects. We also conditionally render the NextStepButton based on the locationShowCreditCardStep prop
// to address the potential difference in UI

import { useState } from 'react';

import styled from '@emotion/styled';
import { isUpcoming } from '@modernanimal/common';
import { FilterSelections } from '@modernanimal/common/src/web/SmartBookingFlow/smartBookingFlow.helpers';
import { DateTime } from 'luxon';
import { useSearchParams } from 'react-router-dom';

import TestNextStepButton from './TestNextStepButton';
import { LocationPartsFragment, CustomerMembershipTierEnum } from '../../__generated__/graphql';
import AppointmentCalendar from '../../AppointmentSelection/AppointmentCalendar';
import AppointmentDateSlots from '../../AppointmentSelection/AppointmentDateSlots';
import AppointmentHeader from '../../AppointmentSelection/AppointmentHeader';
import { useLoadRegionLocationsOnMount, useWatchAvailableSlots } from '../../AppointmentSelection/hooks';
import { Slot } from '../../AppointmentSelection/index';
import NextStepButton from '../../AppointmentSelection/NextStepButton';
import { trackGTMPurchaseEvent } from '../../helpers';
import { createPatientRelatedEntities } from '../../patientOnboarding/patientOnboarding.helpers';
import useWebBookingStore, { AppointmentStoreType } from '../../store';

const useAppointmentSelectionState = (appointment: AppointmentStoreType) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialLocationId = searchParams.get('location');
    const promoCode = searchParams.get('promoCode');

    // Needs to be declared above `getHasPreselectedAppointmentSlot` function
    const [availableDates, setAvailableDates] = useState<string[]>([]);

    const getHasPreselectedAppointmentSlot = (): boolean => {
        /*
        We store the selected appointment slot in local storage so if the user starts an
        in-progress booking flow, their previously selected slot will be preselected. However,
        we shouldn't preselect that slot if 1) it's not available anymore, 2) we're looking at
        a different location than the preselected slot's location, or 3) the preselected slot date
        is in the past.
        */
        if (!appointment.slot) return false;

        // If preselected appointment slot exists, make sure it's still valid.
        const isSameLocation = initialLocationId === appointment.slot?.location.id;
        const isFutureDate = !!appointment.slot?.start && isUpcoming(appointment.slot?.start);
        return isSameLocation && isFutureDate;
    };

    const hasPreselectedAppointmentSlot = getHasPreselectedAppointmentSlot();
    const [appointmentFilters, setAppointmentFilters] = useState<FilterSelections>({
        selectedDate: appointment.slot?.start && hasPreselectedAppointmentSlot
            ? DateTime.fromISO(appointment.slot.start)
            : DateTime.local(),
        selectedLocationIds: [initialLocationId ?? ''],
        selectedRegionId: '',
        selectedDateDoctorIds: [],
        selectedStaffId: '',
    });
    const [availableSlots, setAvailableSlots] = useState<Slot[] | undefined>(undefined);
    const [disabledDates, setDisabledDates] = useState<string[]>([]);
    const [hasLoadedAvailability, setHasLoadedAvailability] = useState(false);
    const [locations, setLocations] = useState<LocationPartsFragment[]>([]);
    const [selectedLocation, setSelectedLocation] = useState<LocationPartsFragment | undefined>(undefined);
    const [selectedSlot, setSelectedSlot] = useState<Slot | null>(
        hasPreselectedAppointmentSlot ? appointment.slot : null
    );

    const addDisabledDate = (disabledDate: string) => {
        setDisabledDates((previousDates) => (
            [...previousDates, disabledDate]
        ));
    };

    const addDisabledDates = (disabledDates: string[]) => {
        setDisabledDates((previousDates) => (
            [...previousDates, ...disabledDates]
        ));
    };

    const resetDisabledDates = () => {
        setDisabledDates([]);
    };

    const selectLocation = (location: LocationPartsFragment) => {
        setSelectedLocation(location);
        setAppointmentFilters((previousFilters) => (
            { ...previousFilters, selectedLocationIds: [location.id] }
        ));

        searchParams.set('location', location.id);
        setSearchParams(searchParams);

        setSelectedSlot(null);
        setAvailableSlots(undefined);
    };

    const setFirstAvailableSlotDate = (hasLoadedAvailability: boolean, selectedDate: FilterSelections['selectedDate'] | null) => {
        setHasLoadedAvailability(hasLoadedAvailability);
        setAppointmentFilters((previousFilters) => (
            { ...previousFilters, selectedDate: selectedDate ?? previousFilters.selectedDate }
        ));
    };

    const setInitialRegionLocations = (
        locations: LocationPartsFragment[],
        selectedLocation: LocationPartsFragment | undefined,
        regionId: string,
    ) => {
        setLocations(locations);
        setSelectedLocation(selectedLocation);
        setAppointmentFilters((previousFilters) => (
            { ...previousFilters, selectedRegionId: regionId }
        ));
    };

    const setSelectedDateAppointmentFilter = (selectedDate: FilterSelections['selectedDate']) => {
        setAppointmentFilters((previousFilters) => (
            { ...previousFilters, selectedDate }
        ));
    };

    const updateAppointmentFiltersFromCalendar = (filters: Partial<FilterSelections>) => {
        setAppointmentFilters((previousFilters) => (
            { ...previousFilters, ...filters }
        ));
        setSelectedSlot(null);
        setAvailableSlots(undefined);
    };

    return {
        actions: {
            addDisabledDate,
            addDisabledDates,
            resetDisabledDates,
            selectLocation,
            updateAppointmentFiltersFromCalendar,
            setAvailableDates,
            setAvailableSlots,
            setFirstAvailableSlotDate,
            setInitialRegionLocations,
            setLocations,
            setSelectedDateAppointmentFilter,
            setSelectedSlot,
        },
        state: {
            appointmentFilters,
            availableDates,
            availableSlots,
            disabledDates,
            hasLoadedAvailability,
            hasPreselectedAppointmentSlot,
            locations,
            selectedLocation,
            selectedSlot,
            promoCode,
        },
    };
};

const TestAppointmentSelection = (props: {
    goToNextStep: ()=> void;
    locationShowCreditCardStep?: boolean;
}) => {
    const { goToNextStep, locationShowCreditCardStep = false } = props;
    const [isCreatingEntities, setIsCreatingEntities] = useState(false);

    const {
        webBookingStoreAppointment,
        updateWebBookingStoreAppointment,
        client,
        patient,
        updatePatient,
        externalClinic,
        isMedicalRecordRequested,
        updateIsMedicalRecordRequested,
        isMembershipCreated,
        updateIsMembershipCreated,
        updateAppointment
    } = useWebBookingStore((state) => ({
        webBookingStoreAppointment: state.appointment,
        updateWebBookingStoreAppointment: state.setAppointment,
        client: state.client,
        patient: state.patient,
        updatePatient: state.setPatient,
        externalClinic: state.externalClinic,
        isMedicalRecordRequested: state.isMedicalRecordRequested,
        updateIsMedicalRecordRequested: state.setIsMedialRecordsRequested,
        isMembershipCreated: state.isMembershipCreated,
        updateIsMembershipCreated: state.setIsMembershipCreated,
        updateAppointment: state.setAppointment
    }));

    const {
        actions: {
            addDisabledDate,
            addDisabledDates,
            resetDisabledDates,
            selectLocation,
            updateAppointmentFiltersFromCalendar,
            setAvailableDates,
            setAvailableSlots,
            setFirstAvailableSlotDate,
            setInitialRegionLocations,
            setSelectedDateAppointmentFilter,
            setLocations,
            setSelectedSlot,
        },
        state: {
            appointmentFilters,
            availableSlots,
            availableDates,
            disabledDates,
            hasLoadedAvailability,
            hasPreselectedAppointmentSlot,
            locations,
            selectedLocation,
            selectedSlot,
        },
    } = useAppointmentSelectionState(webBookingStoreAppointment);

    const { isLoadingRegionLocations } = useLoadRegionLocationsOnMount({
        appointmentFilters,
        setLocations,
        setInitialRegionLocations,
    });

    useWatchAvailableSlots({
        addDisabledDate,
        appointmentFilters,
        availableDates,
        hasLoadedAvailability,
        resetDisabledDates,
        setAvailableSlots,
        setSelectedDateAppointmentFilter,
    });

    const handleNextStepButtonClick = () => {
        updateWebBookingStoreAppointment({ ...webBookingStoreAppointment, slot: selectedSlot });
    };

    const handleTestBookingFlow = async () => {
        setIsCreatingEntities(true);

        try {
            // First update the appointment in the store
            handleNextStepButtonClick();

            // Then create all the patient-related entities
            const createdAppointmentId = await createPatientRelatedEntities(
                patient,
                client,
                externalClinic,
                isMedicalRecordRequested,
                isMembershipCreated,
                { ...webBookingStoreAppointment, slot: selectedSlot }, // Use the latest appointment data
                updatePatient,
                updateIsMedicalRecordRequested,
                updateIsMembershipCreated,
                updateAppointment,
            );

            setIsCreatingEntities(false);

            if (createdAppointmentId) {
                trackGTMPurchaseEvent(createdAppointmentId, CustomerMembershipTierEnum.PayAsYouGo);
                goToNextStep();
            }
        } catch (error) {
            console.error('Error creating appointment:', error);
            setIsCreatingEntities(false);
        }
    };

    const isNextStepButtonDisabled = !availableSlots || selectedSlot === null;
    return (
        <AppointmentSelectionContainer>
            <AppointmentSelectionBody>
                <AppointmentHeader
                    appointmentFilters={appointmentFilters}
                    locations={locations}
                    selectedLocation={selectedLocation ?? null}
                    selectLocation={selectLocation}
                />
                <AppointmentCalendar
                    addDisabledDates={addDisabledDates}
                    appointmentFilters={appointmentFilters}
                    disabledDates={disabledDates}
                    hasPreselectedAppointmentSlot={hasPreselectedAppointmentSlot}
                    loading={isLoadingRegionLocations}
                    selectedLocation={selectedLocation}
                    updateAppointmentFiltersFromCalendar={updateAppointmentFiltersFromCalendar}
                    setAvailableDates={setAvailableDates}
                    setFirstAvailableSlotDate={setFirstAvailableSlotDate}
                />
                <AppointmentDateSlots
                    appointmentFilters={appointmentFilters}
                    availableSlots={availableSlots}
                    location={selectedLocation}
                    selectedSlot={selectedSlot ?? undefined}
                    setSelectedSlot={setSelectedSlot}
                />
            </AppointmentSelectionBody>
            {!locationShowCreditCardStep
                ? (
                    <TestNextStepButton
                        disabled={isNextStepButtonDisabled || isCreatingEntities}
                        loading={isCreatingEntities}
                        onClick={handleTestBookingFlow}
                    />
                )
                : (
                    <NextStepButton
                        disabled={isNextStepButtonDisabled}
                        goToNextStep={goToNextStep}
                        onClick={handleNextStepButtonClick}
                    />
                )}
        </AppointmentSelectionContainer>
    );
};

export default TestAppointmentSelection;

const AppointmentSelectionContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 32px 24px 0 24px;
    gap: 8px;
    flex: 1;
    justify-content: space-between;
`;

const AppointmentSelectionBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 38px;
`;
